import { forwardRef } from 'react'
import { cn } from './cn'

export type CardVariant = 'default' | 'flat'
export type CardPadding = 'none' | 'sm' | 'md' | 'lg'

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
	variant?: CardVariant
	padding?: CardPadding
	interactive?: boolean
}

const paddingMap: Record<CardPadding, string> = {
	none: '',
	sm: 'p-3',
	md: 'p-4',
	lg: 'p-6',
}

const variantMap: Record<CardVariant, string> = {
	default: 'card-refined',
	flat: 'bg-base-100 rounded-2xl border border-base-300',
}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
	{ variant = 'default', padding = 'lg', interactive, className, children, ...rest },
	ref,
) {
	return (
		<div
			ref={ref}
			className={cn(
				variantMap[variant],
				paddingMap[padding],
				interactive && 'cursor-pointer hover:shadow-soft-lg',
				className,
			)}
			{...rest}
		>
			{children}
		</div>
	)
})

export function CardHeader({ className, children, ...rest }: React.HTMLAttributes<HTMLDivElement>) {
	return (
		<div className={cn('flex items-center gap-3 text-lg font-semibold mb-4 pb-3 border-b border-base-300', className)} {...rest}>
			{children}
		</div>
	)
}

export function CardBody({ className, children, ...rest }: React.HTMLAttributes<HTMLDivElement>) {
	return (
		<div className={cn('space-y-4', className)} {...rest}>
			{children}
		</div>
	)
}

export function CardFooter({ className, children, ...rest }: React.HTMLAttributes<HTMLDivElement>) {
	return (
		<div className={cn('flex items-center justify-end gap-3 pt-4 mt-4 border-t border-base-300', className)} {...rest}>
			{children}
		</div>
	)
}
